import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { HbrOption, HbrSelect } from '@harbor/elements-react-standalone';
import StbButton from '../security-toolbox-ui/StbButton';
import { asSingleValue } from '../../support/utils';
import { useLabRuns, useLabScenarios } from '../../hooks/lab-hooks';
import {
  LabRun,
  LabRunState,
  labPhaseLabel,
  labRunStateSentiment,
  labRunSummary,
} from '../../support/labService';
import LabStartRunModal from './LabStartRunModal';
import { LabCard, LabDate, LabPanelState, LabProgressBar, LabStatusTag } from './LabShared';

export type LabRunListProps = {
  /** Fix the list to one session, e.g. inside a session page. */
  sessionKey?: string;
  onOpen?: (run: LabRun) => void;
};

const RUN_STATES: LabRunState[] = [
  'pending',
  'bootstrap',
  'setup',
  'validate',
  'passed',
  'failed',
  'error',
  'cancelled',
  'expired',
];

/**
 * Runs, filtered.
 *
 * "Open runs only" is the default view because that is the set anybody acts on during an
 * event; a finished run is a record, not a thing to watch. What comes back is scoped by the
 * caller's identity on the backend - a run belonging to a session you are not part of is not
 * in this list and is a 404 if you go looking for it directly.
 */
const LabRunList: React.FC<LabRunListProps> = ({ sessionKey, onOpen }) => {
  const navigate = useNavigate();
  const [openOnly, setOpenOnly] = useState(true);
  const [state, setState] = useState<'' | LabRunState>('');
  const [scenarioSlug, setScenarioSlug] = useState('');
  const [startOpen, setStartOpen] = useState(false);

  const { data: scenarios } = useLabScenarios();
  const { data: runs, isLoading, error, refetch } = useLabRuns({
    ...(sessionKey ? { session_key: sessionKey } : {}),
    ...(scenarioSlug ? { scenario: scenarioSlug } : {}),
    ...(state ? { state } : {}),
    ...(openOnly && !state ? { open: true } : {}),
  });

  const open = (run: LabRun) => {
    if (onOpen) {
      onOpen(run);
      return;
    }

    navigate(`/lab/runs/${run.id}`);
  };

  return (
    <>
      <LabCard
        title="Lab runs"
        subtitle="One attempt at one scenario in one session."
        actions={
          <>
            <StbButton role="default" size="small" onClick={async () => refetch()}>
              Refresh
            </StbButton>
            <StbButton role="add" size="small" onClick={async () => setStartOpen(true)}>
              Start a run
            </StbButton>
          </>
        }
      >
        <div className="mb-3 flex flex-wrap items-end gap-3">
          <div className="min-w-[200px]">
            <HbrSelect
              value={scenarioSlug}
              size="small"
              onHbr-change={(evt: any) => setScenarioSlug(asSingleValue(evt.target.value))}
            >
              <HbrOption value="">All scenarios</HbrOption>
              {(scenarios || []).map((scenario) => (
                <HbrOption value={scenario.slug} key={scenario.slug}>
                  {scenario.name}
                </HbrOption>
              ))}
              <div slot="label">Scenario</div>
            </HbrSelect>
          </div>

          <div className="min-w-[180px]">
            <HbrSelect
              value={state}
              size="small"
              onHbr-change={(evt: any) => setState(asSingleValue(evt.target.value) as LabRunState)}
            >
              <HbrOption value="">Any state</HbrOption>
              {RUN_STATES.map((option) => (
                <HbrOption value={option} key={option}>
                  {option}
                </HbrOption>
              ))}
              <div slot="label">State</div>
            </HbrSelect>
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={openOnly}
              disabled={!!state}
              onChange={(evt) => setOpenOnly(evt.target.checked)}
            />
            <span className="text-gray-700">
              Open runs only
              {state && <span className="ml-1 text-xs text-gray-500">(state filter wins)</span>}
            </span>
          </label>
        </div>

        <LabPanelState
          loading={isLoading}
          error={error}
          empty={!runs?.length}
          emptyMessage="No runs match that."
          loadingLabel="Loading runs..."
        >
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-xs uppercase tracking-wide text-gray-500">
                  <th className="px-3 py-2 font-semibold">Run</th>
                  <th className="px-3 py-2 font-semibold">State</th>
                  <th className="px-3 py-2 font-semibold">Progress</th>
                  <th className="px-3 py-2 font-semibold">Score</th>
                  <th className="px-3 py-2 font-semibold">Last activity</th>
                  <th className="px-3 py-2 font-semibold text-right">Actions</th>
                </tr>
              </thead>

              <tbody>
                {(runs || []).map((run) => {
                  const summary = labRunSummary(run);

                  return (
                    <tr key={run.id} className="border-b border-gray-100 align-top">
                      <td className="px-3 py-2">
                        <div className="font-medium">{run.scenario_name || run.scenario_slug}</div>
                        <div className="text-xs text-gray-500">
                          {run.session_name || run.session_key}
                          {run.attempt > 1 ? ` • attempt ${run.attempt}` : ''}
                        </div>
                        {run.for_user_email && (
                          <div className="text-xs text-gray-500">{run.for_user_email}</div>
                        )}
                      </td>

                      <td className="px-3 py-2 whitespace-nowrap">
                        <LabStatusTag label={run.state} sentiment={labRunStateSentiment(run.state)} />
                        <div className="text-xs text-gray-500">{labPhaseLabel(run.phase)}</div>
                        {run.steps_failed > 0 && (
                          <div className="text-xs text-red-700">
                            {`${run.steps_failed} failed`}
                          </div>
                        )}
                      </td>

                      <td className="px-3 py-2">
                        <LabProgressBar
                          percent={summary.progressPercent}
                          label={summary.progressLabel}
                        />
                      </td>

                      <td className="px-3 py-2">
                        <LabProgressBar
                          percent={summary.scorePercent}
                          label={summary.scoreLabel}
                          sentiment={run.state === 'passed' ? 'success' : 'info'}
                        />
                      </td>

                      <td className="px-3 py-2 whitespace-nowrap">
                        <LabDate value={run.last_activity_at} />
                      </td>

                      <td className="px-3 py-2">
                        <div className="flex items-center justify-end">
                          <StbButton role="request" size="xsmall" onClick={async () => open(run)}>
                            Open
                          </StbButton>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </LabPanelState>
      </LabCard>

      <LabStartRunModal
        open={startOpen}
        sessionKey={sessionKey}
        onClose={() => setStartOpen(false)}
        onStarted={(run) => open(run)}
      />
    </>
  );
};

export default LabRunList;
